/**
 * ImageEditorTabContent - Inline tab content for background removal
 * Extracted from ImageEditorPanel (Sheet drawer migration to tabs)
 *
 * Migration Note: Replaced Sheet overlay with inline tab content for better UX
 * No close button - tabs handle navigation
 */

import { useState, useCallback, useEffect } from "react";

import toast from "react-hot-toast";

import {
  Wand2,
  Download,
  Copy,
  Loader2,
  CheckCircle2,
  ImageOff,
  Sparkles,
  Image as ImageIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { WaveLoader } from "@/components/ui/wave-loader";
import { cn } from "@/lib/utils";
import { useImageEditor, ImageEditResult } from "./use-image-editor";

/** Convert filename to no-background version (e.g., "image.jpg" → "image-nobg.png") */
const getNoBackgroundFilename = (filename: string): string =>
  filename.replace(/\.[^/.]+$/, "-nobg.png");

const GALLERY_RELOAD_DELAY_MS = 500; // Gallery refresh delay

// Interface for the image being edited
interface EditingImage {
  id: string;
  expectedFilename: string;
  aspectRatio: string;
  downloadUrl?: string;
}

interface ImageEditorTabContentProps {
  image: EditingImage;
  projectId: string;
  devContainerId?: string;
  previewUrl?: string;
  onImageUpdated: (newImage?: EditingImage) => void;
}

export function ImageEditorTabContent({
  image,
  projectId,
  devContainerId,
  // previewUrl kept on the props shape for caller compat; no longer used since
  // results are served from permanent R2 CDN URLs.
  previewUrl: _previewUrl,
  onImageUpdated,
}: ImageEditorTabContentProps) {
  const { removeBackground, isProcessing, error, reset } = useImageEditor({
    projectId,
    devContainerId,
  });

  // Local state for result preview
  const [editedImage, setEditedImage] = useState<ImageEditResult | null>(null);

  // Reset state when image changes
  useEffect(() => {
    setEditedImage(null);
    reset();
  }, [image.id, reset]);

  // Handle remove background action. Post-R2 migration the result always
  // carries a permanent `publicUrl`; we surface that URL both in the preview
  // and on the new gallery item. `downloadUrl` (15-min PAR URL) is accepted as
  // a fallback for older NestJS versions.
  const handleRemoveBackground = useCallback(async () => {
    if (!image) return;

    try {
      const editResult = await removeBackground(image.downloadUrl!);
      const resultUrl = editResult.publicUrl || editResult.downloadUrl;

      if (!resultUrl) {
        toast.error("Background removal completed but no URL was returned");
        return;
      }

      const newFilename = getNoBackgroundFilename(image.expectedFilename);
      const newImage: EditingImage = {
        id: editResult.id,
        expectedFilename: newFilename,
        aspectRatio: image.aspectRatio,
        downloadUrl: resultUrl,
      };

      setEditedImage(editResult);
      toast.success("Background removed successfully!");

      setTimeout(() => {
        onImageUpdated(newImage);
      }, GALLERY_RELOAD_DELAY_MS);
    } catch {
      toast.error(error || "Failed to remove background");
    }
  }, [image, removeBackground, error, onImageUpdated]);

  // Handle copy URL
  const handleCopyUrl = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied to clipboard");
    } catch {
      toast.error("Failed to copy URL");
    }
  }, []);

  // Handle download
  const handleDownload = useCallback((url: string) => {
    window.open(url, "_blank");
  }, []);

  // Post-R2 migration: a successful result always carries publicUrl (or the
  // fallback downloadUrl). Local-save mode is no longer used.
  const hasResult = !!(editedImage?.publicUrl || editedImage?.downloadUrl);
  const resultPreviewUrl = editedImage?.publicUrl || editedImage?.downloadUrl;

  return (
    <div className="p-4 space-y-6">
      {/* Original Image Preview */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
            <ImageIcon className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
          </div>
          <h4 className="text-sm font-medium text-foreground">
            Original Image
          </h4>
        </div>
        <div className="relative rounded-xl overflow-hidden border-2 border-muted bg-muted/20">
          {image.downloadUrl ? (
            <img
              src={image.downloadUrl}
              alt={image.expectedFilename}
              className="w-full h-auto max-h-[200px] object-contain"
            />
          ) : (
            <div className="w-full h-[200px] flex items-center justify-center bg-muted/30">
              <ImageOff className="w-8 h-8 text-muted-foreground/40" />
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate font-mono bg-muted/50 px-2 py-1 rounded">
          {image.expectedFilename}
        </p>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
            <Wand2 className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
          </div>
          <h4 className="text-sm font-medium text-foreground">Edit Tools</h4>
        </div>

        {/* Remove Background Button */}
        <Button
          onClick={handleRemoveBackground}
          disabled={isProcessing || !image.downloadUrl}
          className={cn(
            "w-full h-12 text-sm font-medium rounded-xl",
            "bg-gradient-to-r from-purple-500 to-purple-600",
            "hover:from-purple-600 hover:to-purple-700",
            "text-white shadow-md hover:shadow-lg",
            "transition-all duration-200",
            "disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none",
          )}
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Removing Background...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Remove Background
            </>
          )}
        </Button>

        {!image.downloadUrl && (
          <p className="text-xs text-muted-foreground text-center">
            Image is still processing or has no download URL
          </p>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-xl bg-red-50 dark:bg-red-950/40 border-2 border-red-200 dark:border-red-800 p-3">
          <p className="text-sm text-red-700 dark:text-red-400 font-medium">
            {error}
          </p>
        </div>
      )}

      {/* Result Preview */}
      {hasResult && resultPreviewUrl && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
              <CheckCircle2 className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" />
            </div>
            <h4 className="text-sm font-medium text-foreground">Result</h4>
          </div>
          <div className="relative rounded-xl overflow-hidden border-2 border-muted bg-[repeating-conic-gradient(#e5e7eb_0%_25%,#f9fafb_0%_50%)_50%_/_16px_16px] dark:bg-[repeating-conic-gradient(#374151_0%_25%,#1f2937_0%_50%)_50%_/_16px_16px]">
            {/* Checkerboard pattern for transparency */}
            <img
              src={resultPreviewUrl}
              alt="Background removed"
              className="w-full h-auto max-h-[200px] object-contain"
            />
          </div>

          {/* Result Actions — copy/download the permanent R2 public URL. */}
          {resultPreviewUrl && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 h-10 rounded-lg hover:bg-muted hover:border-purple-300 dark:hover:border-purple-700 transition-all duration-200"
                onClick={() => handleCopyUrl(resultPreviewUrl)}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy URL
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 h-10 rounded-lg hover:bg-muted hover:border-purple-300 dark:hover:border-purple-700 transition-all duration-200"
                onClick={() => handleDownload(resultPreviewUrl)}
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Processing State */}
      {isProcessing && <WaveLoader />}
    </div>
  );
}
